import { useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { fetchDocumentConfig } from "../api/documents";
import { governingDepartments } from "../utils/reusabilityGovernance";
import {
  navigationProfileFor, visibleSections, isRowActive,
  sectionId, sectionHasActiveRow, resolveOpenSection, toggleOpenSection,
} from "../utils/navigationProfiles";
import CollapsibleNavSection from "./CollapsibleNavSection";

/**
 * ONE sidebar component. The MENU is configuration (Administration Part 1 §4).
 *
 * This file used to declare the destination vocabulary and three hand-written projections inline,
 * which meant every new department shape was an edit to a rendering component. Both now live in
 * `utils/navigationProfiles.js`:
 *
 *   user → designation → department → navigation profile → allowed items → sidebar
 *
 * so adding a department, or reshaping one department's menu, never touches this file and never
 * produces a second Sidebar component. What is left here is presentation: ask which profile this
 * actor's authority calls for, apply the access predicates, render.
 *
 * ⚠ THE SIDEBAR IS UX, NOT AUTHORIZATION (Part 1 §5). A row's absence hides a door; it does not lock
 * it. Every route carries its own `RoleRoute` guard and every endpoint re-checks the caller
 * server-side — a Property Admin who types a Legal URL is refused by the API, not by this menu.
 */

export default function Sidebar({ open, onNavigate }) {
  const { user } = useAuth();
  const location = useLocation();
  const profile = navigationProfileFor(user);
  // CS-5B — a Properties or Legal Department Admin gets "Document Reusability" only when their department governs a
  // document type, which only the catalogue can say. Read with the SAME query key Document Policy uses; nobody else
  // triggers this read.
  const needsCatalogue = user?.designation === "Department Admin"
    && (user?.department === "Properties" || user?.department === "Legal");
  const catalogue = useQuery({
    queryKey: ["documentConfig"], queryFn: () => fetchDocumentConfig(), enabled: needsCatalogue,
    staleTime: 5 * 60 * 1000, retry: false,
  });
  const context = useMemo(
    () => ({ governingDepartments: catalogue.data ? governingDepartments(catalogue.data.catalogue) : null }),
    [catalogue.data]
  );
  const sections = visibleSections(user, profile.sections, context);
  const allRows = sections.flatMap((group) => group.items);

  // ── WHICH COLLAPSIBLE SECTION IS OPEN (Part 1 §6/§7/§27/§31) ───────────────
  // ONE piece of state for the whole accordion, so "only one open at a time" holds by construction
  // rather than by three booleans agreeing.
  //
  // It records only what the READER last opened. Which section is actually open is decided by
  // `resolveOpenSection`, and THE ROUTE WINS there: a section holding the active row is open
  // regardless of this value. That is why no location has to be stored alongside the choice — an
  // earlier draft did exactly that and browser testing caught it resurrecting a stale expansion on
  // browser Forward. Nothing to go stale here.
  // P5-BROWSER-01 — the reader's choice is stored WITH the location object it was made on. React
  // Router hands out a new location object on every navigation (Back and Forward included) and the same
  // one across re-renders, so a choice counts only for the navigation it was made in and can never
  // resurrect on Back/Forward — unlike `location.key`, which Back/Forward restore. With no current choice
  // the resolver sees `undefined` and the route's own section opens.
  // Profiles WITHOUT Part 5 sections (the deferred Master Admin's, the generic fallbacks) keep the
  // original behaviour exactly: the choice persists and the route wins.
  const perNavigation = sections.some((group) => group.readerMayOpenOthers);
  const [userChoice, setUserChoice] = useState({ id: null, location: null });
  const userOpenId = perNavigation
    ? (userChoice.location === location ? userChoice.id : undefined)
    : userChoice.id;
  const setUserOpenId = (id) => setUserChoice({ id, location });

  const rowActive = (row) => isRowActive(row, location, allRows);
  const openId = resolveOpenSection({ sections, location, siblings: allRows, userOpenId });

  return (
    <aside className={`admin-sidebar${open ? " open" : ""}`}>
      <div className="sidebar-brand">
        <i className="bi bi-graph-up-arrow" />
        <div>
          <div className="brand-name">VVR ERP</div>
          {/* WHICH AREA AM I IN? — Part 1 §15 asks the Administration sidebar to be visually
              distinguishable. The profile names itself, so the Master Admin reads "Master Admin"
              and a Legal Admin reads "Legal", rather than every actor reading one fixed subtitle. */}
          <div className="brand-sub">{profile.label}</div>
        </div>
      </div>

      {/* Named, because an Administration page carries TWO nav landmarks — this one and the
          breadcrumb trail. Unlabelled, both announce as plain "navigation" and a screen-reader user
          cannot tell them apart in a landmark list. */}
      <nav className="sidebar-nav" aria-label="Main">
        {sections.map((group, index) => {
          // A section becomes collapsible only by DECLARING it. No profile does today (D-13/D-15 are
          // open owner decisions), so every group takes the flat branch below and the rendered
          // sidebar is identical to before Part 1.
          if (group.collapsible) {
            const id = sectionId(group, index);
            return (
              <CollapsibleNavSection
                key={id}
                id={id}
                label={group.section}
                icon={group.icon}
                items={group.items}
                open={openId === id}
                hasActiveRow={sectionHasActiveRow(group, location, allRows)}
                onToggle={() => setUserOpenId(toggleOpenSection(openId, id))}
                isActiveRow={rowActive}
                onNavigate={onNavigate}
              />
            );
          }
          return (
          // Keyed by INDEX as well as heading: a profile may carry more than one unlabelled group
          // (Administration opens with three rows and closes with Notifications), and two `null`
          // headings would otherwise collide on the same React key.
          <div key={group.section || `group-${index}`}>
            {group.section && <div className="nav-section">{group.section}</div>}
            {group.items.map((row) => (
              <NavLink
                key={row.to}
                to={row.to}
                end={row.end}
                /* THE CALLBACK FORM IS LOAD-BEARING. Given a STRING, `NavLink` appends its own
                   "active" class on top of it whenever ITS pathname-only match succeeds — so a
                   computed class could not withhold the highlight, and all three `/users` rows
                   stayed lit. The callback form returns the final class verbatim, which is what lets
                   `isRowActive` be the only opinion. `isActive` is deliberately unused. */
                className={() => `nav-link${isRowActive(row, location, allRows) ? " active" : ""}`}
                /* The highlight is a colour; `aria-current` is the fact. Same single opinion —
                   `isRowActive` — so the visual state and the announced state cannot disagree.

                   `: false`, NEVER `: undefined`. `NavLink` destructures its own prop as
                   `"aria-current": ariaCurrentProp = "page"` — a DEFAULT PARAMETER, which fires on
                   `undefined` — and then applies it wherever ITS pathname-only match succeeds. So
                   `undefined` did not withhold the attribute, it handed NavLink the default: every
                   row sharing a pathname announced itself as the current page while only one was
                   highlighted. Browser-found on `/tasks/queue?view=…`, where four rows share a path.
                   `false` is a real value, so no default applies and React renders
                   `aria-current="false"` — which is valid ARIA for "not the current item". */
                aria-current={isRowActive(row, location, allRows) ? "page" : false}
                onClick={onNavigate}
              >
                <i className={`bi ${row.icon}`} />
                <span>{row.label}</span>
              </NavLink>
            ))}
          </div>
          );
        })}
      </nav>

      <div className="sidebar-foot">
        <div className="small text-truncate">{user?.fullName}</div>
        <div className="small opacity-75 text-truncate">{user?.designation}</div>
        {user?.department && <div className="small opacity-50 text-truncate">{user.department}</div>}
      </div>
    </aside>
  );
}
